import React, { useState, useEffect, useContext } from "react";

import { Trans, useTranslation } from "react-i18next";
import invert from "invert-color";
import Tippy from "@tippyjs/react";
import "tippy.js/themes/light.css";

import { useHistory, useParams } from "react-router-dom";
import OrganizationsAPI from "../../API/organizations.api";
import ProductsAPI from "../../API/products.api";

import "../../assets/CSS/partner-profile.css";
import ExternalProfileHeader from "../../components/Cards/ExternalProfileHeader";
import HelpAndSupportCard from "../../components/Cards/HelpAndSupportCard";
import UserContext from "../../lib/UserContext";
import { PRODUCT_TYPES, ROLES } from "../../utils/constants";
import LazyImage from "../../components/Shared/LazyImage";
import { isEmpty } from "lodash";
import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import NotFoundView from "../../components/Shared/NotFoundView";
import ProductBriefCard from "../../components/Cards/ProductBriefCard";
import { isAuthorized } from "../../utils/RolesHelpers";
import { handleExternalLink } from "../../utils/ExternalLinksHelper";
import ModelCardSkeleton from "../../components/SkeletonLoaders/ModelCardSkeleton";
import LogCapture from "../../utils/LogManagement/LogHelper";

const itemsPerPage = 3;

const PartnerProfile = () => {
    //Data States
    const [organizationInfo, setOrganizationInfo] = useState(null);
    const [relatedModels, setRelatedModels] = useState([]);
    const [relatedModelsTotalCount, setRelatedModelsTotalCount] = useState(0);
    const [currentModelsPage, setCurrentModelsPage] = useState(1);

    //General States
    const [isInfoLoading, setInfoIsLoading] = useState(false);
    const [isProductsLoading, setIsProductsLoading] = useState(false);
    const [idNotFound, setIdNotFound] = useState(false);

    const [redirectToEditProfile, setRedirectToEditProfile] = useState(false);
    const { t } = useTranslation();
    const history = useHistory();
    const context = useContext(UserContext);

    const params = useParams();
    const ORGANIZATION_NAME = params.name;

    useEffect(() => {
        if (!ORGANIZATION_NAME) {
            history.push("/dashboard");
        } else {
            getOrganizationInfo().then((result) => {
                if (isEmpty(result)) {
                    setIdNotFound(true);
                } else {
                    verifyPartnerAdmin(result.id);
                    getPartnerProducts(0, result.id);
                }
            });
        }
    }, []);

    const getOrganizationInfo = async (name = ORGANIZATION_NAME) => {
        setInfoIsLoading(true);
        const result = await OrganizationsAPI.getExternalOrganizationInfo(name);
        setOrganizationInfo(result);
        setInfoIsLoading(false);
        return result;
    };

    const getPartnerProducts = async (offset = 0, organizationId) => {
        setIsProductsLoading(true);
        try {
            const result = await ProductsAPI.getProductsPartner(
                organizationId || organizationInfo.id,
                itemsPerPage,
                offset
            );
            setRelatedModelsTotalCount(result?.totalCount || 0);
            setRelatedModels((prevModels) => [...prevModels, ...result?.data]);
            setIsProductsLoading(false);
            LogCapture.info(
                `partners of organization with id ${
                    organizationId || organizationInfo.id
                } is fetched`
            );
        } catch (e) {
            LogCapture.error(
                `partners of organization with id ${
                    organizationId || organizationInfo.id
                } can not be fetched`
            );
        }
    };

    const handleEditProfile = () => {
        history.push({
            pathname: "/affiliate-profile",
            state: {
                isEdit: true,
                organization: organizationInfo,
            },
        });
    };

    const verifyPartnerAdmin = (organizationId) => {
        const loggedInUser = context.getUser();

        if (
            isAuthorized(
                [ROLES.partnerAdmin, ROLES.support],
                [loggedInUser.role]
            ) &&
            loggedInUser.organization?.id === organizationId
        )
            setRedirectToEditProfile(true);
        else setRedirectToEditProfile(false);
    };

    const coverStyle = {
        "--color": organizationInfo?.backgroundColor || "#3d4871",
    };

    const contactAndSupportFields = [
        {
            icon: "fas fa-comments",
            label: t("contactUs"),
            external: true,
            action: () =>
                handleExternalLink(`mailto:${organizationInfo?.email}`, true),
            disabled: !organizationInfo?.email,
        },
        {
            icon: "fas fa-eye",
            label: t("viewWebsite"),
            external: true,
            action: () => handleExternalLink(organizationInfo?.website),
            disabled: !organizationInfo?.website,
        },
        {
            icon: "fas fa-file-signature",
            label: t("termsAndConditions"),
            action: () =>
                handleExternalLink(organizationInfo?.termsAndConditions),
            disabled: !organizationInfo?.termsAndConditions,
        },
        {
            icon: "fas fa-key",
            label: t("privacyPolicy"),
            action: () => handleExternalLink(organizationInfo?.privacyPolicy),
            disabled: !organizationInfo?.privacyPolicy,
        },
    ];

    const renderModels = () => {
        return (
            relatedModels &&
            relatedModels.map((model, index) => {
                return (
                    <div className="slider-items models-slider" key={index}>
                        <div className="card-table marginless-card shadow relative overflow-hidden model-box h-full">
                            <div className="flex flex-col content-start w-full">
                                <LazyImage
                                    src={
                                        model?.image ||
                                        require("../../assets/images/neural.png")
                                            .default
                                    }
                                    className="mb-0"
                                    alt={`Image of ${model?.productName}`}
                                />
                                {model?.isBundle && (
                                    <div className="top-left-text">
                                        <div className="tag-ele tag-bundle px-2 uppercase">
                                            {PRODUCT_TYPES.bundle}
                                        </div>
                                    </div>
                                )}
                                <div className="text-left mx-3 my-2">
                                    <div className="flex justify-between align-center">
                                        <h4 className="font-semibold related-models-header mr-1">
                                            {model?.productName}
                                        </h4>
                                        <Tippy
                                            trigger="click"
                                            content={
                                                <ProductBriefCard
                                                    product={model}
                                                />
                                            }
                                            theme="light"
                                            allowHTML={true}
                                            animation="scale"
                                            interactive={true}
                                            appendTo={document.body}
                                        >
                                            <h4>
                                                <i className="fas fa-arrow-right text-primary-color-orange cursor-pointer"></i>
                                            </h4>
                                        </Tippy>
                                    </div>
                                    <div className="text-xs related-models-content mb-2">
                                        {model?.materialNames
                                            ? [
                                                  ...new Set(
                                                      model?.materialNames
                                                  ),
                                              ].join(", ")
                                            : ""}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })
        );
    };

    if (idNotFound) return <NotFoundView className="full-screen" />;
    return (
        <>
            <LoadingOverlay active={isInfoLoading} text="Loading...">
                <div
                    id="user-profile"
                    className="user-profile"
                    style={coverStyle}
                >
                    <div className="card-table shadow relative overflow-hidden">
                        <ExternalProfileHeader
                            image={organizationInfo?.image}
                            editAction={
                                redirectToEditProfile ? handleEditProfile : null
                            }
                            editColor={invert(
                                organizationInfo?.backgroundColor || "#3d4871",
                                { black: "#3d4871", white: "#f0f5fa" }
                            )}
                        />
                        <div className="user-content">
                            <div className="company-profile">
                                <div className="company-info">
                                    <div className="discover-description mb-3">
                                        <h2 className="capitalize h1 flex-item">
                                            {organizationInfo?.name || "---"}
                                        </h2>
                                        <ul className="social-list mb-3">
                                            <li className="social-item mb-1 flex flex-col">
                                                <div className="text-sm related-models-content mb-1">
                                                    <Trans i18nKey="location">
                                                        Location
                                                    </Trans>
                                                </div>
                                                <div className="flex-item">
                                                    <i className="fas fa-map-marker-alt mr-2"></i>
                                                    <p>
                                                        {organizationInfo
                                                            ?.country?.label ||
                                                            "---"}
                                                    </p>
                                                </div>
                                            </li>
                                        </ul>

                                        <div className="flex flex-col">
                                            <div className="text-sm related-models-content mb-1">
                                                <Trans i18nKey="about">
                                                    About
                                                </Trans>
                                            </div>
                                            <p>
                                                {organizationInfo?.about ||
                                                    "---"}
                                            </p>
                                        </div>
                                    </div>
                                    {organizationInfo?.linkedin && (
                                        <div className="discover-description company-links mb-10">
                                            <div className="text-sm related-models-content mb-1">
                                                <Trans i18nKey="links">
                                                    Links
                                                </Trans>
                                            </div>
                                            <ul className="social-list">
                                                <li className="social-item mb-1">
                                                    <div
                                                        className="flex-item cursor-pointer"
                                                        onClick={() =>
                                                            handleExternalLink(
                                                                organizationInfo?.linkedin
                                                            )
                                                        }
                                                    >
                                                        <i className="fab fa-linkedin mr-2"></i>
                                                        <p>
                                                            {organizationInfo?.linkedin ||
                                                                "---"}
                                                        </p>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                    )}
                                </div>
                                <div className="company-contact">
                                    <HelpAndSupportCard
                                        fields={contactAndSupportFields}
                                        containerClassName="mb-10"
                                    />
                                </div>
                            </div>

                            <div className="related-models flex-col">
                                <div className="discover-description company-description">
                                    <h2 className="h1 flex-item">
                                        <Trans i18nKey="relatedProducts">
                                            Related Products
                                        </Trans>
                                        <div></div>
                                    </h2>
                                </div>
                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:w-3/5 md:gap-4 lg:grid-cols-3 xl:w-8/12 2xl:grid-cols-3">
                                    {isProductsLoading ? (
                                        <ModelCardSkeleton />
                                    ) : !isEmpty(relatedModels) ? (
                                        renderModels()
                                    ) : (
                                        <div className="text-sm related-models-content mb-1 ml-2">
                                            No products yet
                                        </div>
                                    )}
                                </div>
                                {relatedModelsTotalCount >
                                    relatedModels.length && (
                                    <span>
                                        <p
                                            className="hyperlink ml-2 mt-2"
                                            onClick={() => {
                                                getPartnerProducts(
                                                    currentModelsPage
                                                );
                                                setCurrentModelsPage(
                                                    (prevPage) => prevPage + 1
                                                );
                                            }}
                                        >
                                            <a>
                                                <Trans i18nKey="seeMore">
                                                    see more
                                                </Trans>
                                            </a>
                                        </p>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </LoadingOverlay>
        </>
    );
};

export default PartnerProfile;
